import { useEffect, useMemo, useRef, useState } from "react";
import { Download, RefreshCw, Wifi, WifiOff, Activity, TrendingUp, MapPin, Loader2, Plus, Bell, BellOff, Trash2, Cpu, Copy, SlidersHorizontal, Sun, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from "lucide-react";
import { jsPDF } from "jspdf";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { useLang, useAuth } from "@/App";
import { Reveal, Overline } from "@/components/motion";
import GuidedTour from "@/components/GuidedTour";
import { dictionary } from "@/i18n";
import {
  mergeZones,
  readCache,
  syncAllZones,
  constatCriteria,
  constatVerdict,
  predictionDays,
  weeklyPointsPerPond,
  DEFAULT_THRESHOLDS,
} from "@/offlineData";

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT_STYLE = {
  validated: "border-teal/50 bg-teal/10 text-teal",
  partial: "border-amber-300 bg-amber-50 text-amber-600",
  rejected: "border-red-300 bg-red-50 text-red-500",
};
const CRIT_STYLE = {
  pass: "border-teal/40 bg-teal/5 text-teal",
  partial: "border-amber-300 bg-amber-50 text-amber-600",
  fail: "border-red-300 bg-red-50 text-red-500",
};

const zoneDotIcon = L.divIcon({
  className: "",
  html: '<div class="map-dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const VERDICT_ICON = {
  validated: CheckCircle2,
  partial: AlertTriangle,
  rejected: XCircle,
};

const TH_BOUNDS = { temp: [0, 45], humidity: [0, 100], pressure: [950, 1060], co2: [200, 3000] };

// guided tour spotlight targets, one per translated step
const ORACLE_TOUR_KEY = "dekrbat_oracle_tour_v1";
const TOUR_TARGETS = [
  null,
  '[data-testid="oracle-online-status"]',
  '[data-testid="oracle-sync-btn"]',
  '[data-testid="oracle-field-mode-toggle"]',
  '[data-testid="oracle-add-zone"]',
  ".verdict-ribbon",
  '[data-testid="oracle-install-btn"]',
  null,
];

function DualRange({ k, label, vals, setRange, zoneId }) {
  const [lo, hi] = TH_BOUNDS[k];
  const [vmin, vmax] = vals[k];
  const pct = (v) => ((v - lo) / (hi - lo)) * 100;
  const step = k === "pressure" || k === "co2" ? 5 : 0.5;
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-ink/55">{label}</span>
      <div className="dual-range">
        <div className="track" />
        <div className="band" style={{ left: `${pct(vmin)}%`, width: `${Math.max(pct(vmax) - pct(vmin), 1)}%` }} />
        <input
          data-testid={`oracle-th-${k}-min-${zoneId}`}
          type="range"
          min={lo}
          max={hi}
          step={step}
          value={vmin}
          onChange={(e) => setRange(k, 0, Math.min(Number(e.target.value), vmax - step))}
          aria-label={`${k} min`}
        />
        <input
          data-testid={`oracle-th-${k}-max-${zoneId}`}
          type="range"
          min={lo}
          max={hi}
          step={step}
          value={vmax}
          onChange={(e) => setRange(k, 1, Math.max(Number(e.target.value), vmin + step))}
          aria-label={`${k} max`}
        />
      </div>
      <span className="w-20 shrink-0 text-right font-mono text-[10px] font-bold text-sky-800">
        {vmin}–{vmax}
      </span>
    </div>
  );
}

function MapClickPicker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function ZoneChart({ days, tempLabel, sunLabel }) {
  if (!days?.length) return null;
  const W = 560;
  const H = 150;
  const P = 10;
  const tLo = Math.min(...days.map((d) => d.tmin)) - 1;
  const tHi = Math.max(...days.map((d) => d.tmax)) + 1;
  const x = (i) => P + (i / (days.length - 1)) * (W - 2 * P);
  const y = (v) => H - P - ((v - tLo) / (tHi - tLo)) * (H - 2 * P);
  const pts = days.map((d, i) => `${x(i)},${y(d.tmax)}`).join(" ");
  const bw = ((W - 2 * P) / days.length) * 0.5;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="chart">
      {days.map((d, i) => (
        <rect
          key={`b${i}`}
          x={x(i) - bw / 2}
          y={H - P - (d.sunshine / 12) * (H - 2 * P) * 0.55}
          width={bw}
          height={(d.sunshine / 12) * (H - 2 * P) * 0.55}
          rx="1.5"
          fill="#bae6fd"
        />
      ))}
      <polyline points={pts} fill="none" stroke="#0284c7" strokeWidth="2.5" strokeLinejoin="round" />
      {days.map((d, i) => (
        <circle key={`c${i}`} cx={x(i)} cy={y(d.tmax)} r="2.5" fill="#0284c7" />
      ))}
      <text x={P} y={P + 8} fontSize="9" fill="#0284c7" fontFamily="monospace">{tempLabel}</text>
      <text x={W - P} y={P + 8} fontSize="9" fill="#38bdf8" fontFamily="monospace" textAnchor="end">{sunLabel}</text>
    </svg>
  );
}

function AddZoneCard({ o, user, pin, setPin, name, setName, onSave, saving }) {
  return (
    <div data-testid="oracle-add-zone" className="rounded-3xl border border-lined bg-white/85 p-6 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-7">
      <p className="flex items-center gap-2 font-headline text-lg font-bold text-ink">
        <Plus className="h-4 w-4 text-teal" />
        {o.addZoneTitle}
      </p>
      {!user ? (
        <p data-testid="oracle-add-zone-login" className="mt-4 font-mono text-xs leading-relaxed text-ink/55">
          {o.loginToAdd}
        </p>
      ) : (
        <>
          <p className="mt-2 font-mono text-[11px] leading-relaxed text-ink/55">{o.addZoneHint}</p>
          <div data-testid="oracle-zone-map" className="mt-4 overflow-hidden rounded-2xl border border-lined">
            <MapContainer center={[13.5, -88.5]} zoom={3} scrollWheelZoom className="h-64 w-full">
              <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapClickPicker onPick={setPin} />
              {pin && <Marker position={pin} icon={zoneDotIcon} />}
            </MapContainer>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              data-testid="oracle-zone-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={o.zoneNamePh}
              maxLength={80}
              className="min-w-0 flex-1 rounded-full border border-lined bg-white px-4 py-2.5 font-mono text-xs text-ink outline-none transition-colors focus:border-teal"
            />
            <button
              data-testid="oracle-zone-save-btn"
              onClick={onSave}
              disabled={!pin || name.trim().length < 2 || saving}
              className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 font-headline text-xs font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {o.saveZone}
            </button>
            {pin && (
              <span data-testid="oracle-zone-pin-coords" className="font-mono text-[10px] text-ink/45">
                {pin[0].toFixed(3)}°, {pin[1].toFixed(3)}°
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ProbeChart({ readings, o, zoneId }) {
  if (!readings?.length) {
    return (
      <p data-testid={`oracle-probe-empty-${zoneId}`} className="mt-2 font-mono text-[10px] text-ink/45">
        {o.noSensorData}
      </p>
    );
  }
  const W = 560;
  const H = 120;
  const P = 8;
  const series = [
    { key: "temp", labelKey: "temp", color: "#f59e0b", unit: "°C" },
    { key: "humidity", labelKey: "humidity", color: "#38bdf8", unit: "%" },
    { key: "co2_ppm", labelKey: "co2", color: "#0d9488", unit: " ppm" },
  ];
  const x = (i) => P + (i / Math.max(readings.length - 1, 1)) * (W - 2 * P);
  return (
    <div data-testid={`oracle-probe-chart-${zoneId}`}>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" aria-label="probe history">
        {series.map((s) => {
          const vals = readings.map((r) => r[s.key]).filter((v) => v != null);
          if (!vals.length) return null;
          const lo = Math.min(...vals);
          const span = Math.max(...vals) - lo || 1;
          const pts = readings
            .map((r, i) => (r[s.key] == null ? null : `${x(i)},${H - P - ((r[s.key] - lo) / span) * (H - 2 * P)}`))
            .filter(Boolean)
            .join(" ");
          return <polyline key={s.key} points={pts} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" />;
        })}
      </svg>
      <div className="mt-1.5 flex flex-wrap gap-3">
        {series.map((s) => {
          const last = [...readings].reverse().find((r) => r[s.key] != null);
          if (!last) return null;
          return (
            <span key={s.key} className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-ink/55">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
              {o.criteria[s.labelKey]} {Math.round(last[s.key] * 10) / 10}
              {s.unit}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ThresholdEditor({ zone, o, onSave, busy }) {
  const base = { ...DEFAULT_THRESHOLDS, ...(zone.thresholds || {}) };
  const [vals, setVals] = useState(() => JSON.parse(JSON.stringify(base)));
  const setRange = (k, i, v) =>
    setVals((prev) => {
      const next = { ...prev, [k]: [...prev[k]] };
      next[k][i] = v;
      return next;
    });
  return (
    <div data-testid={`oracle-thresholds-${zone.id}`} className="mt-3 rounded-xl border border-sky-200 bg-sky-50/60 p-3.5">
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-sky-700">
        {o.thresholdsTitle} · {o.idealRange}
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3">
        {["temp", "humidity", "pressure", "co2"].map((k) => (
          <DualRange key={k} k={k} label={o.criteria[k]} vals={vals} setRange={setRange} zoneId={zone.id} />
        ))}
        <div className="flex items-center gap-2.5">
          <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] text-ink/55">{o.criteria.sunshine}</span>
          <div className="dual-range">
            <div className="track" />
            <div className="band" style={{ left: 0, width: `${(vals.sunshine / 12) * 100}%` }} />
            <input
              data-testid={`oracle-th-sunshine-${zone.id}`}
              type="range"
              min="0"
              max="12"
              step="0.5"
              value={vals.sunshine}
              onChange={(e) => setVals((p) => ({ ...p, sunshine: Number(e.target.value) }))}
              aria-label="sunshine min"
            />
          </div>
          <span className="w-20 shrink-0 text-right font-mono text-[10px] font-bold text-sky-800">
            ≥ {vals.sunshine} {o.sunMin}
          </span>
        </div>
      </div>
      <button
        data-testid={`oracle-th-save-${zone.id}`}
        onClick={() => onSave(zone, vals)}
        disabled={busy}
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-teal px-4 py-1.5 font-headline text-[11px] font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-50"
      >
        {busy && <Loader2 className="h-3 w-3 animate-spin" />}
        {o.saveZone}
      </button>
    </div>
  );
}

function ZoneCard({ zone, entry, o, lang, onReport, reportBusy, telemetryInfo, onToggleAlerts, onDelete, onConnectSensor, onSaveThresholds, newKey, busyZone }) {
  const [showTh, setShowTh] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);
  const today = entry?.days?.find((d) => d.date === todayIso);
  const tele = telemetryInfo?.fresh ? telemetryInfo.reading : null;
  const criteria = entry ? constatCriteria(entry.current, today?.sunshine, tele, zone.thresholds) : null;
  const verdict = constatVerdict(criteria);
  const pred = entry ? predictionDays(entry.days) : [];
  const weekly = weeklyPointsPerPond(pred);
  const maxScore = Math.max(...pred.map((d) => d.score), 1);
  const isCustom = zone.origin === "custom";
  const originLabel = zone.origin === "scenario" ? o.scenarioZone : isCustom ? o.customZone : o.defaultZone;

  return (
    <div data-testid={`oracle-zone-${zone.id}`} className="zone-card relative rounded-3xl border border-lined bg-white/85 p-6 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-7">
      {telemetryInfo?.fresh && (
        <div data-testid={`oracle-scanline-${zone.id}`} className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden="true">
          <div className="xr-scanline" />
        </div>
      )}
      {criteria &&
        (() => {
          const VIcon = VERDICT_ICON[verdict];
          return (
            <div
              data-testid={`oracle-verdict-${zone.id}`}
              className={`verdict-ribbon -mx-6 -mt-6 mb-4 flex items-center justify-center gap-2 rounded-t-3xl border-b px-4 py-2.5 font-headline text-sm font-extrabold uppercase tracking-[0.18em] sm:-mx-7 sm:-mt-7 ${VERDICT_STYLE[verdict]}`}
            >
              <VIcon className="h-4 w-4" />
              {o.verdicts[verdict]}
            </div>
          );
        })()}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-headline text-lg font-bold text-ink">
            <MapPin className="h-4 w-4 text-teal" />
            {zone.name}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
            {originLabel} · {zone.lat.toFixed(2)}°, {zone.lng.toFixed(2)}°
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {telemetryInfo?.fresh && (
            <span
              data-testid={`oracle-sensor-live-${zone.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-teal/50 bg-teal/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-teal"
            >
              <Cpu className="h-3 w-3" />
              {o.sensorLive}
            </span>
          )}
          {!telemetryInfo?.fresh && telemetryInfo?.devices > 0 && (
            <span
              data-testid={`oracle-sensor-offline-${zone.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-amber-600"
            >
              <Cpu className="h-3 w-3" />
              {o.sensorOffline}
            </span>
          )}
        </div>
      </div>

      {!entry && <p className="mt-5 font-mono text-xs text-ink/45">{o.noData}</p>}

      {entry && (
        <>
          <div className="mt-5">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">
                <Activity className="h-3.5 w-3.5" />
                {o.constatTitle}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {(() => {
                  const isToday = new Date(entry.fetchedAt).toDateString() === new Date().toDateString();
                  return (
                    <span
                      data-testid={`oracle-freshness-${zone.id}`}
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] ${
                        isToday ? "border-teal/40 bg-teal/10 text-teal" : "border-amber-300 bg-amber-50 text-amber-600"
                      }`}
                    >
                      {isToday ? o.freshToday : `${o.freshCache} ${new Date(entry.fetchedAt).toLocaleDateString(lang, { dateStyle: "short" })}`}
                    </span>
                  );
                })()}
                <p className="font-mono text-[9px] text-ink/40">
                  {o.cachedAt} {new Date(entry.fetchedAt).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" })}
                </p>
              </div>
            </div>
            <div className={`mt-2 grid grid-cols-2 gap-2 ${criteria.length > 4 ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
              {criteria.map((c) => (
                <div key={c.key} className={`rounded-lg border px-2.5 py-2 text-center ${CRIT_STYLE[c.state]}`}>
                  <p className="font-mono text-xs font-bold">{c.value}</p>
                  <p className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.12em] opacity-70">{o.criteria[c.key]}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">
                <TrendingUp className="h-3.5 w-3.5" />
                {o.predTitle}
              </p>
              <p className="font-mono text-[10px] font-bold text-teal">
                {weekly} <span className="font-normal text-ink/45">{o.weeklyPts}</span>
              </p>
            </div>
            <div className="mt-3 flex items-end gap-1.5">
              {pred.map((d, i) => (
                <div key={i} className="flex flex-1 flex-col items-center gap-1">
                  <span className="font-mono text-[9px] font-bold text-sky-900">{d.score}</span>
                  <div
                    className="w-full rounded-t-md"
                    style={{
                      height: `${Math.max(4, (d.score / maxScore) * 56)}px`,
                      background: "linear-gradient(180deg, #38bdf8 0%, rgba(125,211,252,0.15) 100%)",
                      boxShadow: "0 0 8px rgba(56,189,248,0.35)",
                    }}
                  />
                  <span className="font-mono text-[8px] text-ink/40">
                    {new Date(d.date + "T12:00:00").toLocaleDateString(lang, { weekday: "short" })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-lined bg-bone/70 p-3">
            <ZoneChart days={entry.days} tempLabel={o.chartTemp} sunLabel={o.chartSun} />
          </div>

          <button
            data-testid={`oracle-report-${zone.id}`}
            onClick={() => onReport(zone, tele)}
            disabled={reportBusy === zone.id}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-teal/40 px-5 py-2.5 font-headline text-xs font-semibold text-teal transition-colors duration-300 hover:bg-teal hover:text-white disabled:opacity-60"
          >
            {reportBusy === zone.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {o.reportBtn}
          </button>
        </>
      )}

      {isCustom && (
        <div className="mt-5 border-t border-lined pt-4">
          {telemetryInfo?.devices > 0 && (
            <div className="mb-4 rounded-xl border border-lined bg-bone/70 p-3">
              <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">
                <Cpu className="h-3.5 w-3.5" />
                {o.sensorHistory}
              </p>
              <ProbeChart readings={telemetryInfo.history} o={o} zoneId={zone.id} />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid={`oracle-alerts-toggle-${zone.id}`}
              onClick={() => onToggleAlerts(zone)}
              disabled={busyZone === zone.id}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                zone.alerts_enabled
                  ? "border-teal/40 bg-teal/10 text-teal"
                  : "border-lined bg-white text-ink/45 hover:text-ink"
              }`}
            >
              {zone.alerts_enabled ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
              {zone.alerts_enabled ? o.alertsOn : o.alertsOff}
            </button>
            <button
              data-testid={`oracle-sensor-connect-${zone.id}`}
              onClick={() => onConnectSensor(zone)}
              disabled={busyZone === zone.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-sky-50 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-sky-700 transition-colors duration-300 hover:bg-sky-100 disabled:opacity-50"
            >
              {busyZone === zone.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cpu className="h-3 w-3" />}
              {o.sensorConnect}
            </button>
            <button
              data-testid={`oracle-th-toggle-${zone.id}`}
              onClick={() => setShowTh((v) => !v)}
              disabled={busyZone === zone.id}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                showTh ? "border-teal/40 bg-teal/10 text-teal" : "border-lined bg-white text-ink/45 hover:text-ink"
              }`}
            >
              <SlidersHorizontal className="h-3 w-3" />
              {o.thresholdsTitle}
            </button>
            <button
              data-testid={`oracle-zone-delete-${zone.id}`}
              onClick={() => onDelete(zone)}
              disabled={busyZone === zone.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-red-500 transition-colors duration-300 hover:bg-red-100 disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              {o.deleteZone}
            </button>
          </div>

          {showTh && <ThresholdEditor zone={zone} o={o} onSave={onSaveThresholds} busy={busyZone === zone.id} />}

          {newKey?.zoneId === zone.serverId && (
            <div data-testid={`oracle-sensor-key-${zone.id}`} className="mt-3 rounded-xl border border-teal/30 bg-teal/5 p-3">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-teal">{o.sensorKeyOnce}</p>
              <div className="mt-2 flex items-center gap-2">
                <code data-testid="oracle-sensor-key-value" className="flex-1 break-all rounded-lg bg-white px-2 py-1.5 font-mono text-[11px] text-ink">
                  {newKey.apiKey}
                </code>
                <button
                  data-testid="oracle-sensor-key-copy"
                  onClick={() => navigator.clipboard.writeText(newKey.apiKey)}
                  className="rounded-full border border-teal/40 p-2 text-teal transition-colors hover:bg-teal hover:text-white"
                  aria-label="copy"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-3 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-ink/45">{o.sensorCurl}</p>
              <pre className="mt-1 overflow-x-auto rounded-lg bg-slate-900 p-2.5 font-mono text-[10px] leading-relaxed text-sky-200">{`curl -X POST ${API}/api/telemetry/ingest \\
  -H "X-Device-Key: ${newKey.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"device_id":"${newKey.deviceId}","co2_ppm":610,"temp":28.4,"humidity":62,"pressure":1012}'`}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Oracle() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const o = t.oracle;
  const [scenarioZones, setScenarioZones] = useState([]);
  const [customZones, setCustomZones] = useState([]);
  const [telemetryById, setTelemetryById] = useState({});
  const [cache, setCache] = useState(() => readCache());
  const [sync, setSync] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [installEvt, setInstallEvt] = useState(null);
  const [reportBusy, setReportBusy] = useState(null);
  const [pin, setPin] = useState(null);
  const [zoneName, setZoneName] = useState("");
  const [savingZone, setSavingZone] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [busyZone, setBusyZone] = useState(null);
  const [fieldMode, setFieldMode] = useState(() => localStorage.getItem("dekrbat_field_mode") === "1");
  const [tourRun, setTourRun] = useState(false);
  const syncedRef = useRef(new Set());

  const toggleFieldMode = () => {
    const next = !fieldMode;
    setFieldMode(next);
    localStorage.setItem("dekrbat_field_mode", next ? "1" : "0");
  };

  const closeTour = () => {
    setTourRun(false);
    localStorage.setItem(ORACLE_TOUR_KEY, "done");
  };

  // auto-start the guided tour on first visit
  useEffect(() => {
    if (localStorage.getItem(ORACLE_TOUR_KEY)) return;
    const t = setTimeout(() => setTourRun(true), 1600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const bip = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", bip);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("beforeinstallprompt", bip);
    };
  }, []);

  // scenario zones come from saved PSEQ2 runs (needs network once)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/pseq2-runs`, { credentials: "include" });
        if (r.ok) {
          const runs = await r.json();
          const scZ = [];
          for (const run of runs) {
            if (run.lat == null || run.lng == null) continue;
            const id = `sc-${run.lat.toFixed(2)}_${run.lng.toFixed(2)}`;
            if (!scZ.some((z) => z.id === id)) {
              scZ.push({ id, name: run.place || `${run.lat.toFixed(2)}°, ${run.lng.toFixed(2)}°`, lat: run.lat, lng: run.lng, origin: "scenario" });
            }
          }
          setScenarioZones(scZ);
        }
      } catch {}
    })();
  }, []);

  // custom zones are stored server-side, tied to the user account
  useEffect(() => {
    if (!user) {
      setCustomZones([]);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/oracle/zones`, { credentials: "include" });
        if (r.ok) setCustomZones(await r.json());
      } catch {}
    })();
  }, [user]);

  const customAsZones = useMemo(
    () =>
      customZones.map((z) => ({
        id: `cz-${z.id}`,
        serverId: z.id,
        name: z.name,
        lat: z.lat,
        lng: z.lng,
        origin: "custom",
        alerts_enabled: z.alerts_enabled,
        thresholds: z.thresholds,
      })),
    [customZones]
  );

  const zones = useMemo(() => {
    const merged = mergeZones([...scenarioZones, ...customAsZones]);
    return user ? merged : merged.filter((z) => z.origin !== "custom");
  }, [scenarioZones, customAsZones, user]);

  const fetchTelemetry = async (list) => {
    if (!user || !list.length) {
      setTelemetryById({});
      return;
    }
    const out = {};
    for (const z of list) {
      try {
        const r = await fetch(`${API}/api/oracle/zones/${z.serverId}/telemetry`, { credentials: "include" });
        if (r.ok) {
          const body = await r.json();
          if (body.devices > 0) {
            try {
              const h = await fetch(`${API}/api/oracle/zones/${z.serverId}/telemetry/history?hours=48`, { credentials: "include" });
              if (h.ok) body.history = await h.json();
            } catch {}
          }
          out[z.id] = body;
        }
      } catch {}
    }
    setTelemetryById(out);
  };

  useEffect(() => {
    fetchTelemetry(customAsZones);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customAsZones, user]);

  const runSync = async (zoneList) => {
    if (!navigator.onLine || sync || !zoneList.length) return;
    setSync({ done: 0, total: zoneList.length });
    await syncAllZones(zoneList, (done, total) => setSync({ done, total }));
    setCache(readCache());
    setSync(null);
  };

  // background-download weather for every new zone (on install/visit and when zones are added)
  useEffect(() => {
    const fresh = zones.filter((z) => !syncedRef.current.has(z.id));
    if (!fresh.length || !navigator.onLine) return;
    fresh.forEach((z) => syncedRef.current.add(z.id));
    runSync(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones]);

  const lastSync = useMemo(() => {
    const ts = Object.values(cache).map((e) => e.fetchedAt).filter(Boolean).sort();
    return ts.length ? ts[ts.length - 1] : null;
  }, [cache]);

  const saveZone = async () => {
    if (!pin || zoneName.trim().length < 2 || savingZone) return;
    setSavingZone(true);
    try {
      const r = await fetch(`${API}/api/oracle/zones`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: zoneName.trim(), lat: pin[0], lng: pin[1] }),
      });
      if (!r.ok) throw new Error("save failed");
      const { id } = await r.json();
      setCustomZones((prev) => [{ id, name: zoneName.trim(), lat: pin[0], lng: pin[1], alerts_enabled: true }, ...prev]);
      setPin(null);
      setZoneName("");
      toast.success(o.zoneSaved);
    } catch {
      toast.error(o.saveFailed);
    } finally {
      setSavingZone(false);
    }
  };

  const toggleAlerts = async (zone) => {
    const next = !zone.alerts_enabled;
    setBusyZone(zone.id);
    try {
      const r = await fetch(`${API}/api/oracle/zones/${zone.serverId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ alerts_enabled: next }),
      });
      if (!r.ok) throw new Error("patch failed");
      setCustomZones((prev) => prev.map((z) => (z.id === zone.serverId ? { ...z, alerts_enabled: next } : z)));
    } catch {
      toast.error(o.saveFailed);
    } finally {
      setBusyZone(null);
    }
  };

  const deleteZone = async (zone) => {
    setBusyZone(zone.id);
    try {
      const r = await fetch(`${API}/api/oracle/zones/${zone.serverId}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("delete failed");
      setCustomZones((prev) => prev.filter((z) => z.id !== zone.serverId));
      toast.success(o.zoneDeleted);
    } catch {
      toast.error(o.saveFailed);
    } finally {
      setBusyZone(null);
    }
  };

  const saveThresholds = async (zone, thresholds) => {
    setBusyZone(zone.id);
    try {
      const r = await fetch(`${API}/api/oracle/zones/${zone.serverId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thresholds }),
      });
      if (!r.ok) throw new Error("thresholds failed");
      setCustomZones((prev) => prev.map((z) => (z.id === zone.serverId ? { ...z, thresholds } : z)));
      toast.success(o.thresholdsSaved);
    } catch {
      toast.error(o.saveFailed);
    } finally {
      setBusyZone(null);
    }
  };

  const connectSensor = async (zone) => {
    setBusyZone(zone.id);
    try {
      const r = await fetch(`${API}/api/oracle/zones/${zone.serverId}/devices`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: `${zone.name} — IoT` }),
      });
      if (!r.ok) throw new Error("device failed");
      const d = await r.json();
      setNewKey({ zoneId: zone.serverId, apiKey: d.api_key, deviceId: d.device_id });
      fetchTelemetry(customAsZones);
    } catch {
      toast.error(o.saveFailed);
    } finally {
      setBusyZone(null);
    }
  };

  const zoneReport = async (zone, tele) => {
    const entry = cache[zone.id];
    if (!entry) return;
    setReportBusy(zone.id);
    try {
      const pdfLang = ["es", "en", "pt", "fr"].includes(lang) ? lang : "en";
      const ol = dictionary[pdfLang].oracle;
      const doc = new jsPDF();
      let logoData = null;
      try {
        logoData = await fetch("/dekrbat-logo-deep.png")
          .then((r) => r.blob())
          .then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }));
        doc.addImage(logoData, "PNG", 14, 12, 52, 7.7);
      } catch {}
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(3, 105, 161);
      doc.text(`Dekrbat Oracle — ${zone.name}`, 14, 36);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(90, 105, 98);
      doc.text(`${ol.cachedAt} ${new Date(entry.fetchedAt).toLocaleString(pdfLang)} · ${zone.lat.toFixed(3)}, ${zone.lng.toFixed(3)}${tele ? ` · ${ol.sensorLive}` : ""}`, 14, 43);
      doc.setDrawColor(56, 189, 248);
      doc.setLineWidth(0.8);
      doc.line(14, 47, 196, 47);

      // holographic brand watermark
      try {
        if (logoData) {
          doc.setGState(new doc.GState({ opacity: 0.05 }));
          for (let i = 0; i < 3; i++) doc.addImage(logoData, "PNG", 55, 80 + i * 70, 100, 14.8);
          doc.setGState(new doc.GState({ opacity: 1 }));
        }
      } catch {}

      const todayIso = new Date().toISOString().slice(0, 10);
      const today = entry.days.find((d) => d.date === todayIso);
      const criteria = constatCriteria(entry.current, today?.sunshine, tele, zone.thresholds);
      const verdict = constatVerdict(criteria);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(3, 105, 161);
      doc.text(`${ol.constatTitle}: ${ol.verdicts[verdict]}`, 14, 58);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 28);
      criteria.forEach((c, i) => {
        doc.text(`${ol.criteria[c.key]}: ${c.value} (${c.state})`, 14, 67 + i * 6.5);
      });

      let y = 67 + criteria.length * 6.5 + 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(3, 105, 161);
      doc.text(ol.predTitle, 14, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 28);
      const pred = predictionDays(entry.days);
      pred.forEach((d, i) => {
        doc.text(`${d.date} — score ${d.score}/100 · ${d.sunshine.toFixed(1)}h · ${d.tmax.toFixed(1)}C`, 14, y + 9 + i * 6.5);
      });
      y += 9 + pred.length * 6.5 + 6;
      doc.setFont("helvetica", "bold");
      doc.text(`${weeklyPointsPerPond(pred)} ${ol.weeklyPts}`, 14, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 130, 125);
      doc.text(doc.splitTextToSize(dictionary[pdfLang].pseq2.report.footer, 180), 14, 280);

      doc.save(`oracle-${zone.id}.pdf`);
    } finally {
      setReportBusy(null);
    }
  };

  const install = () => {
    if (installEvt) {
      installEvt.prompt();
      setInstallEvt(null);
    }
  };

  return (
    <section data-testid="oracle-page" className={`emerald-mesh grain-overlay relative min-h-screen overflow-hidden pb-24 pt-20 sm:pt-28${fieldMode ? " field-mode" : ""}`}>
      <div className="grid-pattern absolute inset-0" aria-hidden="true" />
      <div className="hero-glow absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>{o.overline}</Overline>
          <h1 data-testid="oracle-title" className="mt-4 max-w-3xl font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-5xl">
            {o.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">{o.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span
              data-testid="oracle-online-status"
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${
                online ? "border-teal/40 bg-teal/10 text-teal" : "border-amber-300 bg-amber-50 text-amber-600"
              }`}
            >
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {online ? o.online : o.offline}
            </span>
            <button
              data-testid="oracle-install-btn"
              onClick={install}
              className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 font-headline text-xs font-bold text-white transition-colors duration-300 hover:bg-deep"
              title={installEvt ? o.install : o.installing}
            >
              <Download className="h-3.5 w-3.5" />
              {o.install}
            </button>
            <button
              data-testid="oracle-sync-btn"
              onClick={() => {
                syncedRef.current = new Set(zones.map((z) => z.id));
                runSync(zones);
              }}
              disabled={!online || !!sync}
              className="inline-flex items-center gap-2 rounded-full border border-teal/40 px-5 py-2.5 font-headline text-xs font-semibold text-teal transition-colors duration-300 hover:bg-teal hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${sync ? "animate-spin" : ""}`} />
              {sync ? `${o.syncing} ${sync.done}/${sync.total}` : o.syncNow}
            </button>
            <button
              data-testid="oracle-field-mode-toggle"
              onClick={toggleFieldMode}
              aria-pressed={fieldMode}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-headline text-xs font-semibold transition-colors duration-300 ${
                fieldMode
                  ? "border-sky-600 bg-sky-600 text-white shadow-lg shadow-sky-600/30"
                  : "border-sky-300 bg-white/80 text-sky-700 hover:bg-sky-50"
              }`}
              title={o.fieldMode}
            >
              <Sun className="h-3.5 w-3.5" />
              {o.fieldMode}
            </button>
            <button
              data-testid="oracle-tour-replay"
              onClick={() => setTourRun(true)}
              className="inline-flex items-center gap-2 rounded-full border border-lined bg-white/80 px-5 py-2.5 font-headline text-xs font-semibold text-ink/55 transition-colors duration-300 hover:text-teal"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              {o.tour.replay}
            </button>
            <span className="font-mono text-[10px] text-ink/45">
              {o.lastSync}: {lastSync ? new Date(lastSync).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" }) : o.never}
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal delay={0.05}>
            <AddZoneCard o={o} user={user} pin={pin} setPin={setPin} name={zoneName} setName={setZoneName} onSave={saveZone} saving={savingZone} />
          </Reveal>
          {zones.map((zone, i) => (
            <Reveal key={zone.id} delay={0.05 * ((i + 1) % 4)}>
              <ZoneCard
                zone={zone}
                entry={cache[zone.id]}
                o={o}
                lang={lang}
                onReport={zoneReport}
                reportBusy={reportBusy}
                telemetryInfo={telemetryById[zone.id]}
                onToggleAlerts={toggleAlerts}
                onDelete={deleteZone}
                onConnectSensor={connectSensor}
                onSaveThresholds={saveThresholds}
                newKey={newKey}
                busyZone={busyZone}
              />
            </Reveal>
          ))}
        </div>
        <GuidedTour
          steps={o.tour.steps.map((s, i) => ({ ...s, target: TOUR_TARGETS[i] }))}
          labels={o.tour}
          run={tourRun}
          onClose={closeTour}
        />
      </div>
    </section>
  );
}
